import { useState, useCallback, useEffect } from 'react';
import { suppressApiTimeoutToast, enableApiTimeoutToast } from '../apiClient/apiClient';

export interface UseRequestProcessingModalReturn {
  isModalOpen: boolean;
  modalType: 'timeout' | 'error' | 'createError' | 'upchargeError' | 'deactivateError' | null;
  showTimeoutModal: () => void;
  showErrorModal: () => void;
  showCreateErrorModal: () => void;
  showUpchargeErrorModal: () => void;
  showDeactivateErrorModal: () => void;
  closeModal: () => void;
  handleCloseAndRefresh: () => void;
}

interface UseRequestProcessingModalProps {
  onRefresh?: () => void;
}

export const useRequestProcessingModal = ({
  onRefresh,
}: UseRequestProcessingModalProps = {}): UseRequestProcessingModalReturn => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<
    'timeout' | 'error' | 'createError' | 'upchargeError' | 'deactivateError' | null
  >(null);

  // Suppress the API timeout toast when this hook is initialized
  useEffect(() => {
    suppressApiTimeoutToast();

    // Re-enable the toast when the component unmounts
    return () => {
      enableApiTimeoutToast();
    };
  }, []);

  const showTimeoutModal = useCallback(() => {
    setModalType('timeout');
    setIsModalOpen(true);
  }, []);

  const showErrorModal = useCallback(() => {
    setModalType('error');
    setIsModalOpen(true);
  }, []);

  const showCreateErrorModal = useCallback(() => {
    setModalType('createError');
    setIsModalOpen(true);
  }, []);

  const showUpchargeErrorModal = useCallback(() => {
    setModalType('upchargeError');
    setIsModalOpen(true);
  }, []);

  const showDeactivateErrorModal = useCallback(() => {
    setModalType('deactivateError');
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalType(null);
  }, []);

  const handleCloseAndRefresh = useCallback(() => {
    closeModal();
    onRefresh?.();
  }, [closeModal, onRefresh]);

  return {
    isModalOpen,
    modalType,
    showTimeoutModal,
    showErrorModal,
    showCreateErrorModal,
    showUpchargeErrorModal,
    showDeactivateErrorModal,
    closeModal,
    handleCloseAndRefresh,
  };
};
